"use client";

import * as React from "react";

/**
 * Minimal, SSR-safe IntersectionObserver hook.
 * Reveals once when element enters the viewport.
 */
export function useInViewOnce<T extends Element>(rootMargin = "0px 0px -10% 0px") {
  const [seen, setSeen] = React.useState(false);
  const ref = React.useRef<T | null>(null);

  React.useEffect(() => {
    if (seen) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      const id = window.setTimeout(() => setSeen(true), 0);
      return () => window.clearTimeout(id);
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setSeen(true);
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin, threshold: 0.1 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [seen, rootMargin]);

  return { ref, seen };
}
